import SRPProvider, { useFundCentre, usePart } from "@/providers/SRP";
import {
	Fragment,
	type PropsWithChildren,
	useCallback,
	useEffect,
} from "react";
import { FundExplorerAPIResponseSchema } from "./api.types";
import {
	MSG_INIT_FUNDS,
	MSG_LOADING_CONFIG,
	MSG_LOADING_FUNDS,
	MSG_READY_FUNDS,
} from "./constants";
import { StoreProvider, useExplorerStore } from "./store";
import {
	type BaseFundExplorerConfig,
	BaseFundExplorerConfigSchema,
	GenericComponent,
	type KernelProps,
} from "./types";
import { DisplayWrap, FiltersWrap, SearchWrap } from "./wrap";
import GenericWrap from "./wrap/GenericWrap";

function Kernel({
	layout: Layout,
	modules,
	initialState,
	...props
}: KernelProps) {
	const init = useExplorerStore((store) => store.init);
	const loadingStatus = useExplorerStore((store) => store.loadingStatus);
	const doSetLoadingStatus = useExplorerStore(
		(store) => store.doSetLoadingStatus,
	);
	const { data: config, isLoading: isLoadingConfig } = usePart(true, {
		id: props.configId,
		version: props.version,
		language: props.languageId,
	});
	const { data: fundsData, isLoading: isLoadingFunds } = useFundCentre(
		true,
		{ id: props.apiId },
		FundExplorerAPIResponseSchema,
	);

	const renderSlot = useCallback(
		(slot: string) => {
			const slotInserts = modules
				.filter((ins) => ins.slot === slot)
				.sort((m1, m2) => {
					const m1Rank = m1.rank ?? 0;
					const m2Rank = m2.rank ?? 0;
					return m1Rank - m2Rank;
				});
			if (slotInserts.length === 0) return null;
			return slotInserts.map((ins, i) => {
				const key = `${slot}-${ins.component.type}-${i}`;
				switch (ins.component.type) {
					case "display":
						return <DisplayWrap key={key} component={ins.component.node} />;
					case "filter":
						return <FiltersWrap key={key} component={ins.component.node} />;
					case "search":
						return <SearchWrap component={ins.component.node} key={key} />;
					case "generic":
						return (
							<GenericWrap
								component={ins.component.node}
								props={ins.component.props ?? {}}
								key={key}
							/>
						);
					default:
						return null;
				}
			});
		},
		[modules],
	);

	useEffect(() => {
		if (!config) {
			isLoadingConfig && doSetLoadingStatus(MSG_LOADING_CONFIG);
			return;
		}
		if (!fundsData) {
			isLoadingFunds && doSetLoadingStatus(MSG_LOADING_FUNDS);
			return;
		}
		doSetLoadingStatus(MSG_INIT_FUNDS);
		if (BaseFundExplorerConfigSchema.parse(config)) {
			init(config as BaseFundExplorerConfig, fundsData);
		}
		doSetLoadingStatus(MSG_READY_FUNDS);
	}, [
		isLoadingConfig,
		config,
		isLoadingFunds,
		fundsData,
		init,
		doSetLoadingStatus,
	]);

	if (!config) return null;
	if (!fundsData) return null;

	return <Layout renderSlot={renderSlot} />;
}

export default function KernelWrap({
	children,
	tenant,
	...props
}: PropsWithChildren<KernelProps>) {
	const srpProps = {
		url: props.url,
		baseParams: {
			audience: props.audienceId,
			language: props.languageId,
			version: props.version,
			action: props.action,
		},
	};
	return (
		<SRPProvider {...srpProps}>
			<StoreProvider
				initialState={props.initialState}
				srpProps={srpProps}
				tenant={tenant}
			>
				<Kernel {...props}>{children}</Kernel>
			</StoreProvider>
		</SRPProvider>
	);
}
